<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, EmptyState, FormField, Modal, Select, Spinner, Textarea, useToast } from '@drama/ui'
import { clearNovelDramaAssetLearning, listNovelDramaAssets, previewNovelDramaAsset, saveNovelDramaAssetPolicy } from '../../../api/novel/drama.js'
import { useNovelPageText } from '../novel-page-text.js'

const props = defineProps({
  packId: { type: String, default: '' },
  packTitle: { type: String, default: '' },
})
const emit = defineEmits(['close'])
const { t, te } = useI18n()
const toast = useToast()
const { apiErrorLine, dateTime } = useNovelPageText()
const tr = (key, params) => t(`novelDramaAdmin.${key}`, params ?? {})
const items = ref([])
const loading = ref(false)
const busy = ref('')
const error = ref('')
const drafts = reactive({})
const previews = reactive({})
const preview_errors = reactive({})
const previewing = ref('')
let request_id = 0
let preview_request_id = 0

const policy_options = computed(() => ['auto', 'vidu', 'vivix'].map(value => ({ value, label: tr(`assets.policy.${value}`) })))
const asset_key = item => `${item.image_hash}:${item.config_hash}`
const image_source = item => item.image_uri || previews[asset_key(item)] || ''
const provider_label = value => value ? (value === 'auto' ? tr('assets.policy.auto') : (te(`novelDramaAdmin.providers.${value}`) ? tr(`providers.${value}`) : value)) : tr('common.none')
const status_label = value => te(`novelDramaAdmin.assets.status.${value}`) ? tr(`assets.status.${value}`) : (value || tr('common.none'))
const status_variant = value => ({ ready: 'success', failed: 'danger', invalid: 'danger', processing: 'info', preparing: 'info', submitting: 'info', pending: 'warning' })[value] || 'neutral'
const timestamp = value => value ? dateTime(value) : tr('common.none')
const evidence_text = value => typeof value === 'string' ? value : JSON.stringify(value, null, 2)

watch(() => props.packId, () => {
  request_id += 1
  preview_request_id += 1
  items.value = []
  error.value = ''
  busy.value = ''
  previewing.value = ''
  Object.keys(drafts).forEach(key => delete drafts[key])
  Object.keys(previews).forEach(key => delete previews[key])
  Object.keys(preview_errors).forEach(key => delete preview_errors[key])
  if (props.packId) load()
}, { immediate: true })

async function load() {
  const pack_id = props.packId
  const current_request = ++request_id
  loading.value = true
  error.value = ''
  try {
    const response = await listNovelDramaAssets(pack_id)
    if (current_request !== request_id || props.packId !== pack_id) return
    items.value = response.items
    for (const item of response.items) {
      const key = asset_key(item)
      const draft = drafts[key]
      const unchanged = !draft || (draft.manual_policy === draft.original_policy && draft.note === draft.original_note)
      if (unchanged) {
        const manual_policy = item.manual_policy || 'auto'
        const note = item.manual_note || ''
        drafts[key] = { manual_policy, note, original_policy: manual_policy, original_note: note }
      }
    }
  } catch (failure) {
    if (current_request === request_id) error.value = apiErrorLine(failure)
  } finally {
    if (current_request === request_id) loading.value = false
  }
}

async function preview(item) {
  if (previewing.value || busy.value || loading.value) return
  const pack_id = props.packId
  const key = asset_key(item)
  const current_request = ++preview_request_id
  previewing.value = key
  delete preview_errors[key]
  try {
    const response = await previewNovelDramaAsset(pack_id, { image_hash: item.image_hash, config_hash: item.config_hash })
    if (props.packId === pack_id && current_request === preview_request_id) previews[key] = response.image_uri
  } catch (failure) {
    if (props.packId === pack_id && current_request === preview_request_id) preview_errors[key] = apiErrorLine(failure)
  } finally {
    if (props.packId === pack_id && current_request === preview_request_id) previewing.value = ''
  }
}

async function update(item, clear_learned = false) {
  if (busy.value || loading.value) return
  const pack_id = props.packId
  const key = asset_key(item)
  busy.value = key
  error.value = ''
  try {
    const identity = { image_hash: item.image_hash, config_hash: item.config_hash }
    if (clear_learned) await clearNovelDramaAssetLearning(pack_id, identity)
    else await saveNovelDramaAssetPolicy(pack_id, { ...identity, manual_policy: drafts[key].manual_policy, note: drafts[key].note.trim() })
    if (props.packId !== pack_id) return
    if (!clear_learned) delete drafts[key]
    toast.success(tr(clear_learned ? 'assets.cleared' : 'assets.saved'))
    await load()
  } catch (failure) {
    if (props.packId === pack_id) error.value = apiErrorLine(failure)
  } finally {
    if (props.packId === pack_id) busy.value = ''
  }
}

function close(open) {
  if (!open && !busy.value) emit('close')
}
</script>

<template>
  <Modal :open="!!packId" :title="tr('assets.title')" width="1040px" :close-on-backdrop="!busy" @update:open="close">
    <div class="drama-assets" data-testid="novel-drama-assets">
      <header class="drama-assets__header">
        <div>
          <h3>{{ packTitle }}</h3>
          <p class="drama-assets__muted">{{ tr('assets.description') }}</p>
        </div>
        <Button variant="secondary" size="sm" :loading="loading" :disabled="!!busy || !!previewing" data-testid="novel-drama-assets-refresh" @click="load">{{ tr('common.refresh') }}</Button>
      </header>
      <p v-if="error" class="drama-assets__error" role="alert">{{ error }}</p>
      <div v-if="loading && !items.length" class="drama-assets__loading"><Spinner :label="tr('common.loading')" /><span>{{ tr('common.loading') }}</span></div>
      <EmptyState v-else-if="!loading && !items.length" :title="tr('assets.empty')" />
      <article v-for="item in items" :key="asset_key(item)" class="drama-asset" :data-testid="`novel-drama-asset-${item.image_hash}-${item.config_hash}`">
        <div class="drama-asset__image">
          <img v-if="image_source(item)" :src="image_source(item)" :alt="tr('assets.imageAlt', { character: item.character_name || item.character_id })" loading="lazy">
          <div v-else class="drama-asset__preview">
            <span class="drama-assets__muted">{{ tr('assets.noImage') }}</span>
            <Button size="sm" variant="secondary" :loading="previewing === asset_key(item)" :disabled="!!previewing || !!busy || loading" :data-testid="`novel-drama-preview-${item.image_hash}-${item.config_hash}`" @click="preview(item)">{{ tr('assets.loadPreview') }}</Button>
            <p v-if="preview_errors[asset_key(item)]" class="drama-assets__error" role="alert">{{ preview_errors[asset_key(item)] }}</p>
          </div>
        </div>
        <div class="drama-asset__body">
          <header class="drama-asset__header">
            <h4>{{ item.character_name || item.character_id }} · {{ item.location_name || item.location_id || tr('assets.defaultImage') }}</h4>
            <span class="drama-assets__muted">{{ timestamp(item.updated_at) }}</span>
          </header>
          <div class="drama-asset__providers">
            <div v-for="provider in item.providers" :key="provider.provider" class="drama-asset__provider">
              <strong>{{ provider_label(provider.provider) }}</strong>
              <Badge size="sm" :variant="status_variant(provider.status)">{{ status_label(provider.status) }}</Badge>
              <code v-if="provider.error_code" class="drama-assets__error">{{ provider.error_code }}</code>
            </div>
          </div>
          <dl class="drama-asset__facts">
            <div><dt>{{ tr('assets.learnedProvider') }}</dt><dd>{{ provider_label(item.learned_provider) }}</dd></div>
            <div><dt>{{ tr('assets.effectiveProvider') }}</dt><dd>{{ item.effective_provider ? provider_label(item.effective_provider) : tr('assets.policy.auto') }}</dd></div>
          </dl>
          <form v-if="drafts[asset_key(item)]" class="drama-asset__form" @submit.prevent="update(item)">
            <FormField :label="tr('assets.manualPolicy')">
              <Select v-model="drafts[asset_key(item)].manual_policy" :options="policy_options" :disabled="!!busy" :aria-label="tr('assets.policyFor', { character: item.character_name || item.character_id })" :data-testid="`novel-drama-policy-${item.image_hash}-${item.config_hash}`" block />
            </FormField>
            <FormField :label="tr('assets.note')">
              <Textarea v-model="drafts[asset_key(item)].note" :rows="2" :maxlength="2000" :disabled="!!busy" :aria-label="tr('assets.note')" :placeholder="tr('assets.notePlaceholder')" />
            </FormField>
            <div class="drama-asset__actions">
              <Button type="submit" size="sm" :loading="busy === asset_key(item)" :disabled="!!busy || loading" :data-testid="`novel-drama-policy-save-${item.image_hash}-${item.config_hash}`">{{ tr('common.save') }}</Button>
              <Button v-if="item.learned_provider" variant="secondary" size="sm" :disabled="!!busy || loading" :data-testid="`novel-drama-clear-learned-${item.image_hash}-${item.config_hash}`" @click="update(item, true)">{{ tr('assets.clearLearned') }}</Button>
            </div>
          </form>
          <details v-if="item.evidence" class="drama-asset__evidence"><summary>{{ tr('assets.evidence') }}</summary><pre>{{ evidence_text(item.evidence) }}</pre></details>
          <details class="drama-asset__evidence"><summary>{{ tr('assets.identity') }}</summary><dl class="drama-asset__identity"><dt>{{ tr('assets.imageHash') }}</dt><dd><code>{{ item.image_hash }}</code></dd><dt>{{ tr('assets.configHash') }}</dt><dd><code>{{ item.config_hash }}</code></dd></dl></details>
        </div>
      </article>
    </div>
    <template #footer><Button variant="ghost" :disabled="!!busy" @click="emit('close')">{{ tr('common.close') }}</Button></template>
  </Modal>
</template>

<style scoped>
.drama-assets { display: grid; gap: var(--drama-space-lg); }
.drama-assets__header, .drama-asset__header { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: start; gap: var(--drama-space-md); }
.drama-assets__header h3, .drama-assets__header p, .drama-asset__header h4 { margin: 0; }
.drama-assets__header p { margin-top: var(--drama-space-sm); }
.drama-assets__muted { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-assets__error { color: var(--drama-color-danger); overflow-wrap: anywhere; }
.drama-assets__loading { display: flex; align-items: center; gap: var(--drama-space-sm); }
.drama-asset { display: grid; grid-template-columns: minmax(120px, 180px) minmax(0, 1fr); gap: var(--drama-space-lg); padding: var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); }
.drama-asset__image { display: grid; place-items: center; align-self: start; overflow: hidden; min-height: 120px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); }
.drama-asset__image img { display: block; width: 100%; max-height: 240px; object-fit: contain; }
.drama-asset__preview { display: grid; justify-items: center; gap: var(--drama-space-sm); padding: var(--drama-space-md); text-align: center; }
.drama-asset__preview p { margin: 0; font-size: var(--drama-font-size-sm); }
.drama-asset__body { display: grid; gap: var(--drama-space-md); min-width: 0; }
.drama-asset__header h4 { font-size: var(--drama-font-size-md); overflow-wrap: anywhere; }
.drama-asset__providers { display: flex; flex-wrap: wrap; gap: var(--drama-space-md); }
.drama-asset__provider { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); }
.drama-asset__facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-md); margin: 0; }
.drama-asset__facts div { display: grid; gap: var(--drama-space-xs); }
.drama-asset__facts dt, .drama-asset__identity dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-asset__facts dd, .drama-asset__identity dd { margin: 0; overflow-wrap: anywhere; }
.drama-asset__form { display: grid; gap: var(--drama-space-md); }
.drama-asset__actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.drama-asset__evidence summary { cursor: pointer; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-asset__evidence summary:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
.drama-asset__evidence pre { margin: var(--drama-space-sm) 0 0; padding: var(--drama-space-md); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); white-space: pre-wrap; overflow-wrap: anywhere; }
.drama-asset__identity { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: var(--drama-space-sm); margin: var(--drama-space-sm) 0 0; }
@media (max-width: 680px) { .drama-asset { grid-template-columns: minmax(0, 1fr); } .drama-asset__image { max-width: 180px; } }
</style>
